import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Scale, FileBadge, Layers, UserRound, Wallet, Calculator, Lock, Trash2, EyeOff } from "lucide-react";
import { PublicShell, SignInButton } from "@/components/PublicShell";
import { StaticHero } from "@/components/StaticHero";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";


const HERO_DEFAULTS = {
  landing_hero_title: "Know where you stand.",
  landing_hero_highlight: "In plain rupees.",
  landing_hero_subtitle: "Add what you own and what you owe. See your net worth, how long your money would last in an emergency, and a simple resilience score — in about 5 minutes.",
};
const STEPS = [
  { icon: UserRound, title: "Sign in & tell us about your home", text: "Google sign-in, then a few details and four monthly numbers." },
  { icon: Wallet, title: "Add what you own and owe", text: "Savings, EPF, gold, property, loans, card dues — rough values are fine." },
  { icon: Calculator, title: "See your results", text: "Net worth, emergency runway, income dependency and a score out of 100." },
];
const GET = [
  { icon: Scale, title: "Your net worth picture", text: "What you own minus what you owe, in lakhs and crores — with a plain line explaining each number." },
  { icon: FileBadge, title: "Personal Net Worth Report", text: "A clean, dated PDF of your snapshot with a unique reference — useful for your own records." },
  { icon: Layers, title: "Holistic Networth Report", text: "Add your Grow Up Once Again Future Readiness report and see your money and readiness for change across five pillars." },
];
const TRUST = [
  { icon: Lock, title: "Private to you", text: "Your entries, reports are visible only to you." },
  { icon: EyeOff, title: "No bank logins", text: "We never ask for bank passwords, card numbers, PAN or Aadhaar." },
  { icon: Trash2, title: "Delete any time", text: "Remove your account and data from your profile, in line with the DPDP Act, 2023." },
];

const Grid = ({ items, prefix }) => (
  <div className="mt-8 grid md:grid-cols-3 gap-6">
    {items.map(({ icon: Icon, title, text }, i) => (
      <div key={title} className="rounded-2xl border border-slate-200 bg-white p-7 hover:border-[#0078D4]/40 hover:-translate-y-0.5 transition-[transform,border-color] duration-300 rise-in" style={{ animationDelay: `${i * 80}ms` }} data-testid={`${prefix}-${i}`}>
        <div className="h-11 w-11 rounded-xl bg-[#0078D4]/10 text-[#0078D4] grid place-items-center"><Icon className="h-5 w-5" /></div>
        <h3 className="mt-5 font-bold text-slate-900">{prefix === "landing-step" ? `${i + 1}. ` : ""}{title}</h3>
        <p className="mt-2 text-sm text-slate-600">{text}</p>
      </div>
    ))}
  </div>
);

const Section = ({ title, text, children, tinted, testid }) => (
  <section className={tinted ? "bg-slate-50 border-y border-slate-200" : ""} data-testid={testid}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
      <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{title}</h2>
      {text && <p className="mt-2 text-slate-600 max-w-2xl">{text}</p>}
      {children}
    </div>
  </section>
);

export default function Home() {
  const { config } = useAuth();
  const c = { ...HERO_DEFAULTS, ...(config?.content || {}) };
  useEffect(() => {
    if (sessionStorage.getItem("lpv")) return;
    sessionStorage.setItem("lpv", "1");
    api.post("/events", { event: "landing_page_view" }).catch(() => {});
  }, []);

  return (
    <PublicShell bare>
      <StaticHero content={c} />
      <Section title="How it works" text="Three simple steps, about five minutes." testid="landing-how-section">
        <Grid items={STEPS} prefix="landing-step" />
        <Link to="/how-it-works" className="mt-6 inline-block max-lg:py-3 text-sm font-semibold text-[#0078D4] hover:text-[#005A9E]" data-testid="landing-how-more-link">Read the full walkthrough →</Link>
      </Section>
      <Section tinted title="What you get" text="Clear numbers, and documents you can keep." testid="landing-get-section"><Grid items={GET} prefix="landing-feature" /></Section>
      <Section title="Your data, your control" text="Built with privacy first, in line with India's Digital Personal Data Protection Act, 2023." testid="landing-trust-section">
        <Grid items={TRUST} prefix="landing-trust" />
        <div className="mt-12 rounded-3xl bg-[#005A9E] text-white p-8 sm:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <p className="text-2xl font-extrabold tracking-tight">Ready to see where you stand?</p>
            <p className="mt-1 text-white/80">Free, private and in plain language. Not financial advice.</p>
          </div>
          <SignInButton testid="landing-bottom-signin-button" className="h-12 px-6 bg-white text-[#005A9E] hover:bg-white/90" />
        </div>
      </Section>
    </PublicShell>
  );
}
